import { useContext, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { XMarkIcon } from "@heroicons/react/24/outline";
import DefaultImage from "assets/Images/BrandImage1.png";
import SearchInput from "components/atoms/forms/SearchInput";
import UserMagnetCard from "components/atoms/cards/user/UserMagnetCard";
import Button from "components/atoms/buttons/Button";
import { patch } from "utils/request";
import { CheckIcon } from "components/atoms/Icon";
import Modal from "components/atoms/modal";
import { UserContext } from "components/context/UserContext";
import ErrorMessage from "components/atoms/forms/ErrorMessage";
import { maxNoOfFeaturedMagnets, TOAST_CONFIG, UPDATE_FEATURED_MAGNETS } from "../../../constants";

type Props = {
  inventory: InventoryItem[];
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

function EditFeaturedMagnetsModal({ inventory, open, setOpen }: Props) {
  const { reloadUserData } = useContext(UserContext);

  const [searchText, setSearchText] = useState("");

  const [featuredMagnetIds, setFeaturedMagnetIds] = useState<string[]>(
    inventory.filter((inv: InventoryItem) => inv.is_featured).map((m: InventoryItem) => m.id),
  );

  const closeModal = () => {
    setOpen(false);
  };

  const handleFeaturedMagnetsSubmissionSuccess = () => {
    closeModal();
    reloadUserData();
    toast.success("Featured Magnets Updated Successfully", TOAST_CONFIG);
  };
  const handleFeaturedMagnetsSubmissionError = () => {
    toast.error("Failed to Update Featured Magnets ", TOAST_CONFIG);
  };

  const toggleFeaturedMagnet = (magnetId: string) => {
    const isMagnetFeatured = featuredMagnetIds.includes(magnetId);

    // Remove magnet if already in list.
    if (isMagnetFeatured) {
      setFeaturedMagnetIds((fMagnetIds) => fMagnetIds.filter((mId) => mId !== magnetId));
    } else if (featuredMagnetIds.length < maxNoOfFeaturedMagnets) {
      // Closing curly brace does not appear on the same line as the subsequent block.
      setFeaturedMagnetIds((fMagnetIds) => [...fMagnetIds, magnetId]);
    } else {
      toast.error("Only 12 magnets can be featured", TOAST_CONFIG);
    }
  };

  const submitUpdatedFeaturedMagnets = () => {
    patch(
      UPDATE_FEATURED_MAGNETS,
      {
        featured_inventory_ids: featuredMagnetIds,
      },
      {
        processData: handleFeaturedMagnetsSubmissionSuccess,
        processError: handleFeaturedMagnetsSubmissionError,
      },
    );
  };

  const handleSetSearchText = (text: string) => setSearchText(text);

  const magnetsInInventory = useMemo(() => inventory.filter((inv) => inv.type === "MAGNET"), [inventory]);

  const magnetsToRender = useMemo(
    () =>
      searchText
        ? magnetsInInventory.filter((magnet) => magnet.name.toLowerCase().includes(searchText.toLocaleLowerCase()))
        : magnetsInInventory,
    [searchText],
  );

  useEffect(() => {
    reloadUserData();
  }, []);

  return (
    <Modal open={open} onClose={closeModal}>
      <div
        className="flex max-h-[90vh] w-screen
         max-w-4xl flex-col gap-x-10 gap-y-8 rounded-2xl bg-primary p-7 shadow-modal md:w-[70vw] lg:w-[50vw]"
      >
        <div className="flex justify-between space-x-28">
          <p className="font-satoshi text-xs font-bold leading-9 text-white md:text-2xl">
            {magnetsInInventory.length ? "Select Magnets to Show On Your Fridge" : "No Magnets to Feature"}
          </p>
          <button
            type="button"
            className="hidden rounded-md text-white  hover:text-opacity-70 md:block"
            onClick={closeModal}
          >
            <XMarkIcon className="h-7 w-7" aria-hidden="true" />
          </button>
        </div>
        <div className="my-2 flex hidden justify-end  md:block">
          <div className="w-48 2xl:w-60">
            <SearchInput
              searchText={searchText}
              handleOnChange={handleSetSearchText}
              additionalClasses="px-2 py-1 2xl:px-3 2xl:py-2"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-x-6 gap-y-7  overflow-y-scroll p-2 pt-4  scrollbar-hide sm:grid-cols-3 md:grid-cols-4">
          {magnetsToRender.map((magnet: InventoryItem) => (
            <UserMagnetCard
              key={`allMags${magnet.id}`}
              isClickable
              info={featuredMagnetIds.includes(magnet.id) && <CheckIcon width="14" height="28" />}
              magnetName={magnet.name}
              imageUrl={magnet.image_url || DefaultImage}
              brandName={magnet.brand_name}
              handleOnClick={() => toggleFeaturedMagnet(magnet.id)}
            />
          ))}
        </div>
        <div className="flex items-center justify-between px-4">
          <div>
            {!magnetsInInventory.length && (
              <ErrorMessage errorMessage="Start adding magnets from brands to see featured magnets." />
            )}
            {!!magnetsInInventory.length && featuredMagnetIds.length < 1 && (
              <ErrorMessage errorMessage="Select atleast one featured magnet." />
            )}
          </div>
          <Button
            text="Done"
            onClick={submitUpdatedFeaturedMagnets}
            additionalClasses="xl:text-xs
            2xl:text-sm xl:px-6 xl:py-3 2xl:px-10 2xl:py-5"
            variant="magnetiqSolid"
            disabled={featuredMagnetIds.length < 1 || !magnetsInInventory.length}
          />
        </div>
      </div>
    </Modal>
  );
}
export default EditFeaturedMagnetsModal;
